import React, { useMemo } from 'react';
import type { ReleaseKitLook } from 'fym-shared/src/music24/releaseThemes';
import { AssetStyleId, defaultStyleFor, WaveformShape } from 'fym-shared/src/music24/assetStyles';
import type { StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { lyricVideoStyleOf } from 'fym-shared/src/music24/story/lyricVideo';
import { TypedMemo } from '../../../../common/types';
import { useKitWebFonts } from '../../themes/kitWebFonts';
import { assetFrameVars, FrameLyric, FrameSong, frameLyricOf } from './assetFrameVars';
import { VisualizerFrame } from './VisualizerFrame';
import { LyricFrame } from './LyricFrame';
import { LyricVideoFrame } from './LyricVideoFrame';
import { MusicVideoFrame } from './MusicVideoFrame';
import { MotionArtworkFrame } from './MotionArtworkFrame';

/** The asset types that the Studio draws as a frame of design s (no render call). */
export const FRAME_ASSET_TYPES = ['visualizer', 'lyricVideo', 'musicVideo', 'lyricMoment', 'motionArtwork'] as const;
export type FrameAssetTypeId = (typeof FRAME_ASSET_TYPES)[number];

export function isFrameAssetType(id: StudioThemeId): id is FrameAssetTypeId {
  return (FRAME_ASSET_TYPES as readonly string[]).includes(id);
}

export interface AssetFrameProps {
  assetTypeId: FrameAssetTypeId;
  /** The style of the frame. Absent: the default style of the theme for the asset type. */
  assetStyle?: AssetStyleId;
  waveform?: WaveformShape | null;
  look: ReleaseKitLook;
  palette: readonly string[];
  song: FrameSong;
  /** The sung line. Absent: the title takes its place. */
  lyric?: FrameLyric;
  /** The motion artwork pair takes the height of the box (the step-2 card). */
  fixedHeight?: boolean;
  className?: string;
}

/**
 * One still of a full video, a lyric moment or the motion artwork in the song's theme (designs
 * s1–s5): drawn in the browser from the theme colours of the cover, the type of the theme and the
 * cover. It makes no render call, so it is a sketch of the asset, not the asset.
 */
const AssetFrameComponent: React.FC<AssetFrameProps> = (props) => {
  const {
    assetTypeId,
    assetStyle,
    waveform: pickedWaveform,
    look,
    palette,
    song,
    lyric,
    fixedHeight,
    className,
  } = props;
  useKitWebFonts();
  const vars = useMemo(() => assetFrameVars(look, palette, song.artworkUrl), [look, palette, song.artworkUrl]);
  const fallback = defaultStyleFor(look.themeId, assetTypeId);
  const style = assetStyle ?? fallback?.style ?? 'coverGround';
  const waveform = pickedWaveform ?? (assetStyle === undefined ? (fallback?.waveform ?? null) : null);
  const sung = lyric ?? frameLyricOf([], 0, song.title);

  switch (assetTypeId) {
    case 'visualizer':
      return (
        <VisualizerFrame
          themeId={look.themeId}
          style={style}
          waveform={waveform}
          song={song}
          vars={vars}
          className={className}
        />
      );
    case 'lyricVideo':
      // Designs y and y3: the lyric column on the right, the cover card, the blurred cover or the
      // theme ground by the style (the boxes of the renderer). AI footage keeps the footage frame.
      if (style !== 'aiFootage') {
        return (
          <LyricVideoFrame
            themeId={look.themeId}
            style={lyricVideoStyleOf(style)}
            song={song}
            lyric={sung}
            vars={vars}
            className={className}
          />
        );
      }
      return (
        <LyricFrame
          themeId={look.themeId}
          style={style}
          tall={false}
          song={song}
          lyric={sung}
          vars={vars}
          className={className}
        />
      );
    case 'lyricMoment':
      return (
        <LyricFrame
          themeId={look.themeId}
          style={style}
          tall
          song={song}
          lyric={sung}
          vars={vars}
          className={className}
        />
      );
    case 'musicVideo':
      return (
        <MusicVideoFrame
          themeId={look.themeId}
          song={song}
          caption={lyric === undefined ? null : lyric.current}
          vars={vars}
          className={className}
        />
      );
    case 'motionArtwork':
      return (
        <MotionArtworkFrame
          themeId={look.themeId}
          vars={vars}
          fixedHeight={fixedHeight}
          playing={fixedHeight !== true}
          className={className}
        />
      );
  }
};

export const AssetFrame = TypedMemo(AssetFrameComponent);
